import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BatchChangeRequestsJobType, type MutationRefetchQueries } from "~/common/types";
import { config } from "~/config";
import {
  CreateBatchChangeRequestsJobDocument,
  type CreateBatchChangeRequestsJobMutation,
  type CreateBatchChangeRequestsJobMutationVariables,
  UpdateBatchChangeRequestsJobDocument,
  type UpdateBatchChangeRequestsJobMutation,
  type UpdateBatchChangeRequestsJobMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { BatchChangeRequestJobsFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface Props {
  importProjectId: string;
  batchChangeRequestsJobId?: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
  onSuccess: () => void;
}

export function useBatchChangeRequestsJobForm({
  importProjectId,
  batchChangeRequestsJobId,
  refetchQueriesOnEdit,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const { tenantId } = useTenant();
  const [updateBatchChangeRequestsJob] = useMutation<
    UpdateBatchChangeRequestsJobMutation,
    UpdateBatchChangeRequestsJobMutationVariables
  >(UpdateBatchChangeRequestsJobDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });
  const [createBatchChangeRequestsJob] = useMutation<
    CreateBatchChangeRequestsJobMutation,
    CreateBatchChangeRequestsJobMutationVariables
  >(CreateBatchChangeRequestsJobDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });

  const submit = useCallback(
    async (formData: BatchChangeRequestJobsFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const fn = batchChangeRequestsJobId
          ? updateBatchChangeRequestsJob({
              variables: {
                input: {
                  tenantId,
                  id: batchChangeRequestsJobId,
                  name: formData.name,
                  jobType: formData.jobType,
                  recordComposerDefinitionIds:
                    formData.jobType === BatchChangeRequestsJobType.EntityComposer ||
                    formData.jobType === BatchChangeRequestsJobType.RelationComposer
                      ? formData.recordComposerDefinitionIds
                      : undefined,
                },
              },
            })
          : createBatchChangeRequestsJob({
              variables: {
                input: {
                  tenantId,
                  dataImportProjectId: importProjectId,
                  name: formData.name,
                  jobType: formData.jobType,
                  recordComposerDefinitionIds:
                    formData.jobType === BatchChangeRequestsJobType.EntityComposer ||
                    formData.jobType === BatchChangeRequestsJobType.RelationComposer
                      ? formData.recordComposerDefinitionIds
                      : undefined,
                },
              },
            });

        await fn;

        const successMsg = batchChangeRequestsJobId
          ? t("feature.common.updatedSuccessfully", { model: "batchChangeRequestsJob" })
          : t("feature.common.createdSuccessfully", { model: "batchChangeRequestsJob" });

        toast.success(successMsg);
        onSuccess();
      } catch (e) {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
      } finally {
        toast.dismiss(progressToastId);
        setProcessing(false);
      }
    },
    [
      tenantId,
      importProjectId,
      batchChangeRequestsJobId,
      updateBatchChangeRequestsJob,
      createBatchChangeRequestsJob,
      onSuccess,
      t,
    ],
  );

  return {
    submit,
    processing,
  };
}
